<template>
    <!-- Primary Nav Wrapper -->
    <header ref="primaryNav" :class="['primaryNav__wrapper', {'-minimal' : isMinimal}, { '-fixed': ( isMobileDevice ) }]">

        <!-- Top Section (Logo, Utility Nav, Buttons) -->
        <section :class="['primaryNav__topWrapper', { '-fixed': hasFixedNav }]" ref="sticky">
            <div :class="['primaryNav__top', { '-mobile': isMobileDevice }]">
                <!-- Show logo only when fixed or on mobile -->
                <a class="primaryNav__brandLink" href="/" v-if="hasFixedNav || isMobileDevice">
                    <div class="primaryNav__logo" :class="{ '-compact': hasFixedNav && !isMobileDevice }">
                        <div class="sr-only">Beacon Hill Staffing - Home</div>
                        <icon-main-logo
                            title="Beacon Hill Staffing Home"
                            url="/"
                            animate
                            :show-name="!hasFixedNav || isMobileDevice"
                            :speed="0.01"
                        ></icon-main-logo>
                    </div>
                </a>
                <div v-if="!isMobileDevice || (isMobileDevice && !hasMobileOnlyView)" class="primaryNav__ctaWrapper">
                    <slot name="buttons"></slot>
                </div>

                <!-- Toggle Button -->
                <PrimaryNavToggleButton
                    v-if="isMobileDevice && hasMobileOnlyView"
                    :is-active="showMobileMenu"
                    @toggle="toggleMobile"
                />
            </div>
        </section>

        <!-- Bottom Section (Primary Nav Links, Panels) -->
        <section v-if="!isMinimal" class="primaryNav__bottomWrapper">
            <div :class="['primaryNav__bottom', { '-fixed': hasFixedNav }]">
                <!-- Add logo in desktop view -->
                <a v-if="!isMobileDevice" class="primaryNav__brandLink" href="/">
                    <div class="primaryNav__logo">
                        <div class="sr-only">Beacon Hill Staffing - Home</div>
                        <icon-main-logo title="Beacon Hill Staffing Home" url="/" animate :show-name="!hasFixedNav" :speed="0.01"></icon-main-logo>
                    </div>
                </a>

                <!-- Desktop Nav List -->
                <nav ref="main" role="navigation" aria-label="Main"
                    :class="['primaryNav__mainNavList', { '-fixed': hasFixedNav }]"
                >
                    <ul
                        v-if="!isMobileDevice"
                        :class="[
                            'primaryNav__list',
                            { '-desktopOnly': hasMobileOnlyView },
                             { '-fixed': hasFixedNav }
                        ]"
                    >
                        <slot
                            name="desktop"
                            :open="open"
                            :touch="touchHandler"
                            :close="closePanel"
                            :expand="openPanel"
                            @close="closePanel"
                        ></slot>

                    </ul>
                </nav>

                <!-- Utility Wrapper -->
                <div
                    v-if="!isMobileDevice"
                    ref="utilityNav"
                    class="primaryNav__utilityWrapper"
                >
                    <nav
                        :class="[
                            'primaryNav__utilityNav',
                            { '-hidden': hasFixedNav },
                        ]"
                    >
                        <ul class="primaryNav__utilityNavList">
                        <slot name="utility"></slot>
                        </ul>
                    </nav>
                </div>
            </div>
        </section>

        <!-- Mobile Menu -->
        <div ref="mobilePanel" v-if="showMobileMenu && isMobileDevice"  class="primaryNav__mobilePanelWrapper">
            <div
                class="primaryNav__mobilePanel"
                :style="{ 'height' : `${mobilePanelHeight}px` }"
                @keydown="($event) => directionalHandler($event, this.$refs.mobilePanel)"
            >
                <slot name="mobile" :open="open" :toggle="toggleMobile"></slot>
            </div>
        </div>

        <!-- Outside Gradient (for when desktop nav is open) -->
        <transition name="primaryNav__transitionBlur">
            <PrimaryNavBlur
                v-if="panelIsOpen"
                class="primaryNav__outside"
                @click="closePanel"
            ></PrimaryNavBlur>
        </transition>
    </header>
</template>

<script>
import PrimaryNavToggleButton from "./PrimaryNavToggleButton.vue";
import PrimaryNavBlur from "./PrimaryNavBlur.vue"
import IconMainLogo from "@js/components/Icons/IconMainLogo.vue";
import useDirectional from '@js/mixins/directional'

export default {
    components: {
        PrimaryNavToggleButton,
        IconMainLogo,
        PrimaryNavBlur
    },
    mixins: [useDirectional],
    props: {
        logo: {
            type: String,
            required: false,
            default: "",
        },
        logoAlt: {
            type: String,
            required: false,
            default: "",
        },
        isMinimal: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    data() {
        return {
            open: "",
            isMobileDevice: false,
            showMobileMenu: false,
            hasFixedNav: false,
            navIntersectObserver: null,
            screenResizeObserver: null,
            delayTimer: null,
            mobilePanelHeight: 0,
        };
    },
    mounted() {
        // Starts watching for nav interection event
        this.createIntersectionObserver();

        // Starts watching for mobile vs desktop menu view
        this.createResizeObserver();

        // Track height of sticky nav for scroll purposes
        this.updateStickyNavHeight();
    },
    computed: {
        panelIsOpen() {
            return this.open !== "";
        },
        hasMobileOnlyView() {
            return !!this.$slots.mobile;
        },
    },
    methods: {
        toggleDesktopPanel(newOpen) {
            if(this.open === newOpen){
                            clearTimeout(this.delayTimer)
                //return
            }
            if (this.open === "" || this.open !== newOpen) {
                this.openPanel(newOpen);
            } else {
                this.closePanel();
            }
        },
        touchHandler(){
            if (this.open === "" || this.open !== newOpen) {
                this.openPanel(newOpen);
            } else {
                // Go to link
            }
        },
        toggleBodyScroll(){
           // document.body.style.height = (this.showMobileMenu) ? '100vh' : 'auto'
            document.body.style.overflowY = (this.showMobileMenu) ? 'hidden' : 'auto'
        },
        toggleMobile() {
            this.showMobileMenu = !this.showMobileMenu;
            if(this.showMobileMenu){
                this.$nextTick(() => {
                this.updateMobilePanelHeight()

                })
            }
            this.toggleBodyScroll()
        },
        closePanel() {
            clearTimeout(this.delayTimer)
            this.delayTimer = setTimeout(()=> {
                this.open = ""
            }, 500)

        },
        openPanel(newOpen) {
            if(this.open.length){
                clearTimeout(this.delayTimer)
                this.delayTimer = setTimeout(() => {
                    this.open = newOpen
                }, 500)
            }
            else {
                this.open = newOpen
            }

        },
        updateMobilePanelHeight(){
            let heightAbove = this.$refs.primaryNav.offsetHeight
            const alert = document.querySelector('.alert')
            if (alert){
                const rect = alert.getBoundingClientRect();

                const alertInView = (
                    rect.top >= 0 &&
                    rect.left >= 0 &&
                    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
                    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
                )
                if(alertInView){
                    heightAbove = heightAbove + alert.offsetHeight
                }
                else {

                }
            }
           const contentHeight = this.$refs.mobilePanel.offsetHeight
            if((window.innerHeight - heightAbove) > contentHeight){
                this.mobilePanelHeight = window.innerHeight - heightAbove
            }
            else {
                this.mobilePanelHeight = contentHeight
            }
        },
        updateStickyNavHeight() {
            const sticky = this.$refs.sticky
            window.stickyNavHeight = sticky.offsetHeight
        },
        createIntersectionObserver() {
             const options = {
                root: null,
               // rootMargin: "16px",
                //threshold: [0.25]
            };
            this.navIntersectObserver = new IntersectionObserver(([entry]) => {
                if (entry && entry.isIntersecting) {
                    this.hasFixedNav = false;
                } else {
                    this.hasFixedNav = true;
                }
            }, options);

            this.navIntersectObserver.observe(this.$refs.utilityNav);
        },
        createResizeObserver() {
            this.screenResizeObserver = new ResizeObserver((entries) => {
                for (let entry of entries) {
                    const width = entry.contentRect.width;
                    this.isMobileDevice = width < 1000;

                    // No need for the fixed nav on mobile, so get rid of that
                    if(this.isMobileDevice){
                        if(this.navIntersectObserver){
                            this.navIntersectObserver.disconnect()
                            this.navIntersectObserver = null
                        }
                    }
                    else {
                        if(!this.navIntersectObserver){
                            this.$nextTick(()=> {
                                this.createIntersectionObserver()
                            })
                        }
                    }

                    if(!this.isMobileDevice && this.showMobileMenu){
                        this.toggleMobile()
                    }

                    // Update Sticky Nav Height
                    this.updateStickyNavHeight()
                }
            });

            this.screenResizeObserver.observe(this.$el);
        },
    },
};
</script>

